import React from 'react';
import { Users, Calendar, Languages, User } from '../../../icons';

function fmtStat(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

/** Map language code(s) ("en", "en,hi") to display names ("English, Hindi"). */
function langName(code: string): string {
  const codes = code.split(/[,\s]+/).filter(Boolean);
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'language' });
    return codes.map((c) => dn.of(c) || c.toUpperCase()).join(', ');
  } catch {
    return codes.map((c) => c.toUpperCase()).join(', ');
  }
}

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/** Figma demographics stat — icon box + label/value. Reusable cell. */
export function StatCell({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-[10px] bg-[#F4F4F2] text-[#737373]">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-caption-regular text-[#737373]">{label}</div>
        <div className="truncate text-body-md-medium text-[#171717]">{value}</div>
      </div>
    </div>
  );
}

/** Same footprint as `StatCell` (40px icon box + two text lines) so the row
 *  doesn't reflow once real data replaces the skeleton. */
function StatCellSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-2.5">
      <div className="size-10 shrink-0 rounded-[10px] bg-[#F4F4F2]" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="h-3 w-14 rounded-sm bg-[#F4F4F2]" />
        <div className="h-4 w-20 rounded-sm bg-[#F4F4F2]" />
      </div>
    </div>
  );
}

/** Number of KPI slots the demographics row is laid out for — every cell
 *  (real or skeleton) gets 1/4 of the row's width, regardless of how many
 *  are actually present, so 1-2 populated stats don't stretch to fill it. */
const KPI_SLOTS = 4;

interface CreatorDemographicsProps {
  /** Resolved subscriber/follower count. */
  subscribers?: number | null;
  ageGroup?: string | null;
  /** Language code(s), e.g. "en" or "en,hi". */
  language?: string | null;
  /** Gender, e.g. "MALE". */
  gender?: string | null;
  /** Subscribers stat label — "Subscribers" (YouTube) or "Followers" (Instagram). */
  subscribersLabel?: string;
  /** When true, lays the cells out in an even row with thin vertical dividers
   *  between them (Figma drawer-header style). Otherwise wraps with gap spacing. */
  withDividers?: boolean;
  /** Drop the default top margin (for when the row sits inside its own boxed container). */
  flush?: boolean;
  /** When true, renders 4 skeleton placeholders instead of the real stats
   *  (data hasn't resolved yet). */
  isLoading?: boolean;
}

export function CreatorDemographics({
  subscribers,
  ageGroup,
  language,
  gender,
  subscribersLabel = 'Subscribers',
  withDividers = false,
  flush = false,
  isLoading = false,
}: CreatorDemographicsProps) {
  const subs = Number(subscribers ?? 0);

  if (withDividers && isLoading) {
    // Reserve the full 4-slot row up front so it doesn't reflow once data resolves.
    return (
      <div className={`${flush ? '' : 'mt-3'} grid w-full grid-cols-4 items-center divide-x divide-[#E5E5E5]`}>
        {Array.from({ length: KPI_SLOTS }).map((_, i) => (
          <div key={i} className={i === 0 ? 'pr-5' : 'px-5'}>
            <StatCellSkeleton />
          </div>
        ))}
      </div>
    );
  }

  if (!(subs > 0 || ageGroup || language || gender)) return null;

  const cells = [
    subs > 0 && <StatCell key="subs" icon={<Users size={18} />} label={subscribersLabel} value={fmtStat(subs)} />,
    ageGroup && <StatCell key="age" icon={<Calendar size={18} />} label="Age" value={ageGroup} />,
    language && <StatCell key="lang" icon={<Languages size={18} />} label="Language" value={langName(language)} />,
    gender && <StatCell key="gender" icon={<User size={18} />} label="Gender" value={titleCase(gender)} />,
  ].filter(Boolean);

  if (withDividers) {
    // Figma drawer-header: each cell gets a fixed 1/4-of-the-row slot (not
    // flex-1) so 1-2 populated stats stay KPI-tile-sized instead of
    // stretching to fill the whole row; leftover slots are just blank space.
    return (
      <div className={`${flush ? '' : 'mt-3'} grid w-full grid-cols-4 items-center divide-x divide-[#E5E5E5]`}>
        {cells.map((cell, i) => (
          <div key={i} className={i === 0 ? 'pr-5' : 'px-5'}>
            {cell}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-3">{cells}</div>
  );
}
